import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { bannerStore, EnumAlertBannerVariant } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { ComplianceApi } from 'entities/Compliance/api/compliance'
import { type RegulatoryBundleItem } from 'pages/settings/pages/compliance/pages/international/model/RegulatoryBundleItem'
import { SettingsPath } from 'pages/settings/route/settingsPath'

export class InternationalRegulatoryBundlesStore {
  private _itemsMap: Map<number, RegulatoryBundleItem> = new Map()
  private _country: string | null = null
  private _bannerId = 'international_regulatory_bundle_rejected'
  private _disposeRegistrationFailed: IReactionDisposer | null = null
  private _isBannerClosed = true

  constructor() {
    makeAutoObservable(this)

    this.reactionRegistrationFailed()
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get regulatoryBundleCountry() {
    return this._country
  }

  get regulationMobileType() {
    return this.items.find((item) => item.regulation?.numberType === 'mobile')
  }

  get isUKApproved() {
    return this.regulationMobileType?.isStatusApproved
  }

  get pathToCurrentStep() {
    if (this.regulationMobileType?.step === 1) {
      return `/${SettingsPath.compliance.international.businessInfo}`
    }
    if (this.regulationMobileType?.step === 2) {
      return `/${SettingsPath.compliance.international.businessAddress}`
    }
    if (this.regulationMobileType?.step === 3) {
      return `/${SettingsPath.compliance.international.contactInfo}`
    }
    if (this.regulationMobileType?.step === 4) {
      return `/${SettingsPath.compliance.international.finalDetails}`
    }
    if (this.regulationMobileType?.step === 5) {
      return `/${SettingsPath.compliance.international.root}`
    }

    return `/${SettingsPath.compliance.international.businessInfo}`
  }

  setItem = (item: RegulatoryBundleItem) => {
    if (item.id !== null) this._itemsMap.set(item.id, item)
  }

  deleteItem = (id: number) => {
    this._itemsMap.delete(id)
  }

  removeItems = () => {
    this._itemsMap.clear()
  }

  setRegulatoryBundleCountry = (country: string) => {
    this._country = country
  }

  setBannerClosedStatus = (status: boolean) => {
    this._isBannerClosed = status
    this.showErrorsBanner()
  }

  hideErrorsBanner = () => {
    bannerStore.remove(this._bannerId)
  }

  closeErrorsBanner = async () => {
    this.hideErrorsBanner()
    ComplianceApi.closeComplianceBannersSummary().then((data) => {
      this._isBannerClosed = data.data.banners_closed
    })
  }

  showErrorsBanner = () => {
    if (!this.regulationMobileType?.isStatusFailed) return
    if (this._isBannerClosed) return

    bannerStore.add({
      id: this._bannerId,
      action: {
        text: 'Update',
        onAction: () => {
          this.goToBusinessInfo()
          this.hideErrorsBanner()
        },
      },
      actionClose: {
        showCloseBtn: true,
        onAction: this.closeErrorsBanner,
      },
      alert: {
        title:
          'Uh oh! Your regulatory bundle application was rejected. Please update and resubmit to get approved to send texts.',
        variant: EnumAlertBannerVariant.Error,
        ariaLabel: 'Regulatory_Bundle_Application_Was_Rejected',
      },
    })
  }

  goToBusinessInfo = () => {
    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.international.businessInfo}`,
    })
  }

  reactionRegistrationFailed = () => {
    this._disposeRegistrationFailed?.()
    this._disposeRegistrationFailed = reaction(
      () => this.regulationMobileType?.isStatusFailed,
      (status) => {
        if (status) {
          this.showErrorsBanner()
        } else {
          this.hideErrorsBanner()
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  dispose = () => {
    this._disposeRegistrationFailed?.()
  }
}
